import React, { useMemo, useState } from 'react';
import { Search, ClipboardList, ChevronRight, CheckSquare } from 'lucide-react';
import { Button, Select } from '../../../components';
import { Badge, Card, EmptyState, auditStatus, formatDate, inputClass, isoPlusDays } from './shared';

/**
 * The audit register — what is due, what is running, what is overdue.
 *
 * Filtering happens here rather than on the server: an organisation runs tens
 * of audits, not thousands, so the whole register already arrived with the
 * page, and doing it locally keeps the tabs and the search instant.
 *
 * Sorted by due date throughout. A register that sorts by anything else makes
 * the manager work out what is urgent for themselves, which is the one job it
 * exists to do for them.
 */
const AuditRegister = ({ audits, summary, onOpenAudit, onOpenActions, onNewAudit }) => {
  const [mode, setMode] = useState('active');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  const counts = useMemo(() => ({
    active: audits.filter((a) => a.status !== 'completed').length,
    completed: audits.filter((a) => a.status === 'completed').length,
  }), [audits]);

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return audits
      .filter((a) => (mode === 'completed' ? a.status === 'completed' : a.status !== 'completed'))
      .filter((a) => !needle || `${a.title} ${a.owner_name || ''} ${a.area || ''}`.toLowerCase().includes(needle))
      .filter((a) => {
        if (filter === 'all') return true;
        if (filter === 'overdue') return a.overdue;
        // "Due this week" deliberately excludes overdue: they are already
        // called out separately, and counting them twice hides how much is
        // genuinely upcoming.
        if (filter === 'due') return !a.overdue && a.due_date <= isoPlusDays(7);
        return a.status === filter;
      })
      .sort((a, b) => String(a.due_date).localeCompare(String(b.due_date)));
  }, [audits, mode, search, filter]);

  const filterOptions = [
    { value: 'all', label: 'All statuses' },
    ...(mode === 'active' ? [
      { value: 'overdue', label: 'Overdue' },
      { value: 'due', label: 'Due this week' },
      { value: 'in_progress', label: 'In progress' },
      { value: 'scheduled', label: 'Scheduled' },
    ] : []),
  ];

  const clearFilters = () => { setSearch(''); setFilter('all'); };

  return (
    <Card>
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 px-6 pt-6 pb-5">
        <div>
          <h2 className="text-[17px] font-bold text-[#0B1220] dark:text-gray-100">Your audits</h2>
          <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 mt-1.5">
            See what is due, then open an audit to work through it.
          </p>
        </div>

        <div className="flex items-center gap-4 flex-wrap">
          <Button variant="soft" size="sm" leftIcon={<CheckSquare className="w-4 h-4" />} onClick={() => onOpenActions()}>
            Open actions
            <span className="ml-1.5 bg-[#E3ECFD] dark:bg-blue-900/40 rounded px-1.5 py-0.5 text-[11px] tabular-nums">
              {summary?.open_actions ?? 0}
            </span>
          </Button>
          <button
            type="button"
            onClick={() => { setMode('active'); setFilter('overdue'); }}
            className={`text-[12.5px] flex items-center gap-2 hover:underline ${summary?.overdue ? 'text-rose-600 dark:text-rose-400 font-semibold' : 'text-[#64748B] dark:text-gray-400'}`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-current" />
            {summary?.overdue ?? 0} overdue
          </button>
          <span className="w-px h-3.5 bg-[#E3E9F3] dark:bg-gray-700 hidden sm:block" />
          <button
            type="button"
            onClick={() => { setMode('active'); setFilter('due'); }}
            className="text-[12.5px] text-[#64748B] dark:text-gray-400 hover:underline"
          >
            {summary?.due_this_week ?? 0} due this week
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 px-6 pb-5">
        <div className="flex items-center gap-1.5">
          {[['active', 'Active', counts.active], ['completed', 'Completed', counts.completed]].map(([value, label, count]) => (
            <button
              key={value}
              type="button"
              onClick={() => { setMode(value); setFilter('all'); }}
              aria-pressed={mode === value}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-[12.5px] transition-colors ${
                mode === value
                  ? 'bg-[#EFF5FF] dark:bg-blue-900/25 text-blue-600 dark:text-blue-300 font-semibold'
                  : 'text-[#64748B] dark:text-gray-400 hover:bg-[#F5F8FC] dark:hover:bg-gray-800'
              }`}
            >
              {label}
              <span className="text-[11px] opacity-80 tabular-nums">{count}</span>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2.5">
          <div className="relative flex-1 lg:w-[250px]">
            <Search className="w-4 h-4 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              className={`${inputClass} pl-9 py-2`}
              placeholder="Search audits…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search audits"
            />
          </div>
          <Select value={filter} onChange={setFilter} options={filterOptions} size="sm" className="w-[160px]" />
        </div>
      </div>

      {!rows.length ? (
        <EmptyState
          icon={ClipboardList}
          title={audits.length ? 'No matching audits' : 'No audits yet'}
          description={audits.length
            ? 'Try a different search or filter.'
            : 'Schedule your first audit to start recording what you check and what it found.'}
          action={audits.length
            ? <Button variant="secondary" onClick={clearFilters}>Clear filters</Button>
            : <Button variant="primary" onClick={onNewAudit}>New audit</Button>}
        />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px]">
              <thead>
                <tr className="border-t border-[#E3E9F3] dark:border-gray-800">
                  {['Audit', 'Owner', 'Due date', 'Status', 'Checklist', ''].map((heading, i) => (
                    <th
                      key={heading || i}
                      className={`bg-[#F8FAFD] dark:bg-gray-800/40 text-[11px] font-semibold text-[#8391A7] dark:text-gray-400 text-left py-3.5 px-5 ${i === 0 ? 'pl-6' : ''} ${i === 5 ? 'pr-6' : ''}`}
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((audit) => {
                  const status = auditStatus(audit);
                  const progress = audit.total_checks ? (audit.answered / audit.total_checks) * 100 : 0;
                  return (
                    <tr
                      key={audit.id}
                      className="border-t border-[#EEF2F8] dark:border-gray-800 hover:bg-[#FCFDFF] dark:hover:bg-gray-800/30"
                    >
                      <td className="py-5 px-5 pl-6 align-top w-[32%]">
                        <button
                          type="button"
                          onClick={() => onOpenAudit(audit.id)}
                          className="text-[13.5px] font-semibold text-[#24324A] dark:text-gray-100 hover:text-blue-600 text-left"
                        >
                          {audit.title}
                        </button>
                        <p className="text-[11.5px] text-[#96A2B5] dark:text-gray-500 mt-1.5">
                          {audit.area}
                          <span className="mx-1.5">·</span>
                          {audit.repeat_cycle}
                        </p>
                      </td>
                      <td className="py-5 px-5 align-top text-[12.5px] text-[#50627D] dark:text-gray-300">
                        {audit.owner_name || '—'}
                      </td>
                      <td className="py-5 px-5 align-top text-[12.5px]">
                        <span className={audit.overdue ? 'text-rose-600 dark:text-rose-400 font-semibold' : 'text-[#50627D] dark:text-gray-300'}>
                          {formatDate(audit.due_date)}
                        </span>
                      </td>
                      <td className="py-5 px-5 align-top">
                        <Badge tone={status.tone}>{status.label}</Badge>
                        {audit.open_actions > 0 && (
                          <button
                            type="button"
                            onClick={() => onOpenActions(audit.id)}
                            className="block mt-2 text-[11px] text-[#6686B6] dark:text-blue-400 hover:underline text-left"
                          >
                            {audit.open_actions} open {audit.open_actions === 1 ? 'action' : 'actions'}
                          </button>
                        )}
                      </td>
                      <td className="py-5 px-5 align-top min-w-[130px]">
                        <span className="text-[11px] text-[#8192AC] dark:text-gray-500 tabular-nums">
                          {audit.answered} / {audit.total_checks} checks
                        </span>
                        <div className="h-1.5 rounded-full bg-[#EDF2F8] dark:bg-gray-800 mt-2 overflow-hidden">
                          <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
                        </div>
                      </td>
                      <td className="py-5 px-5 pr-6 align-top text-right">
                        <Button
                          size="sm"
                          variant={audit.status === 'in_progress' ? 'primary' : 'secondary'}
                          rightIcon={<ChevronRight className="w-4 h-4" />}
                          onClick={() => onOpenAudit(audit.id)}
                        >
                          {audit.status === 'completed' ? 'View' : audit.status === 'in_progress' ? 'Continue' : 'Start'}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between px-6 py-4 border-t border-[#E3E9F3] dark:border-gray-800 text-[11.5px] text-[#8192AC] dark:text-gray-500">
            <span>{rows.length} {rows.length === 1 ? 'audit' : 'audits'}</span>
            <span>Earliest due first</span>
          </div>
        </>
      )}
    </Card>
  );
};


export default AuditRegister;
